import { getFlagPath } from './FlagUtils.js';
import StringUtils from './StringUtils.js';

/**
 * Markup de l'annuaire des pays (page /pays), partagé par le build et le
 * runtime.
 *
 * La liste est écrite dans le HTML statique par la tâche gulp
 * `prerenderDirectory` : sans ça, les 193 pays n'existaient que côté client
 * (Training.renderDirectory) et la page la plus riche du site était vide pour
 * tout crawler qui n'exécute pas JS.
 *
 * `Training.js` utilise le MÊME générateur en repli (pré-rendu absent), donc les
 * deux sorties sont identiques au caractère près — indispensable, sinon le DOM
 * sauterait au chargement.
 *
 * Module volontairement sans dépendance DOM ni Node pour rester importable des
 * deux côtés.
 */

/** Nombre de drapeaux chargés en `eager` : au-dessus de la ligne de flottaison. */
const EAGER_FLAGS = 12;

const escapeHtml = (s) => String(s)
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;');

/**
 * Regroupe par première lettre (translittérée), comme le fait `app.js` en
 * construisant `countriesByLetter`.
 */
export function groupByLetter(countries, locale = 'fr') {
	const byLetter = {};
	for (const country of countries) {
		const letter = StringUtils.sanitize(country.name.substring(0, 1));
		(byLetter[letter] ||= []).push(country);
	}
	for (const letter of Object.keys(byLetter)) {
		byLetter[letter].sort((a, b) => a.name.localeCompare(b.name, locale));
	}
	return byLetter;
}

export function renderCountryItem(country, { eager = false, href = '' } = {}) {
	const name = escapeHtml(country.name);
	// Chaque pays devient un lien vers sa fiche quand elle existe : c'est ce qui
	// rend les 193 fiches atteignables par le crawl depuis une page unique.
	// `href` n'est fourni qu'au build (la table de slugs n'est pas chargée côté
	// client) — sans lui, l'item reste un simple bloc, comme avant.
	const tag = href ? 'a' : 'div';
	const linkAttrs = href ? ` href="${escapeHtml(href)}"` : '';
	// Flèche en fin de ligne : seulement quand l'item est un vrai lien, pour ne
	// pas promettre une action sur les pays sans fiche publiée.
	const arrow = href
		? '<span class="country-arrow" aria-hidden="true"><svg viewBox="0 -960 960 960"><path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"/></svg></span>'
		: '';
	// Dimensions explicites = pas de reflow quand les 193 drapeaux arrivent
	// (le conteneur CSS fait 3.5rem × 2.5rem, même ratio).
	return `<${tag} class="country-item"${linkAttrs} data-name="${escapeHtml(StringUtils.sanitize(country.name))}" data-code="${escapeHtml(country.code.toLowerCase())}">
            <div class="country-flag">
                <img src="${getFlagPath(country.code)}" alt="${name}" width="56" height="40" decoding="async"${eager ? '' : ' loading="lazy"'}>
            </div>
            <div class="country-info">
                <div class="country-name">${name}</div>
                <div class="country-capital">${escapeHtml(country.capital)}</div>
            </div>
            ${arrow}
        </${tag}>`;
}

/**
 * Annuaire complet : une section par lettre (titre + pays triés).
 * `countries` est la liste plate telle que servie par `countries-{lang}.json`
 * ou telle qu'indexée dans `geoNerdApp.countries`.
 */
/**
 * @param {Array}  countries  liste plate, telle que `countries-{lang}.json`
 * @param {string} locale     'fr' | 'en' — ordre alphabétique et langue des liens
 * @param {object} [linkFor]  code ISO → URL de la fiche pays. Fourni par le
 *                            build uniquement : la table de slugs n'est pas
 *                            chargée côté client, où les items restent inertes.
 */
export function renderLetterSections(countries, locale = 'fr', linkFor = null) {
	const byLetter = groupByLetter(countries, locale);
	let rendered = 0;

	return Object.keys(byLetter).sort().map(letter => {
		const items = byLetter[letter].map(country => {
			const eager = rendered < EAGER_FLAGS;
			rendered++;
			const href = linkFor ? linkFor[country.code.toLowerCase()] || '' : '';
			return renderCountryItem(country, { eager, href });
		}).join('\n');

		return `<div class="letter-section" data-letter="${escapeHtml(letter.toLowerCase())}">
            <h2 class="section-title">${escapeHtml(letter.toUpperCase())}</h2>
${items}
        </div>`;
	}).join('\n');
}
